import React, { useState } from "react";
import { CreateOrganizationSchema } from "@toolkit/mobile-shared";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/button";
import { FieldLabel, TextInput } from "../../../components/ui/FormControls";
import { SurfaceCard } from "../../../components/ui/SurfaceCard";
import { AuthPageLayout } from "./AuthPageLayout";
import { IncomingOrgInvitationsCard } from "./IncomingOrgInvitationsCard";
import { OrgTradeFields } from "./OrgTradeFields";
import type { MainAppPageProps } from "../types";

type OrgSetupPageProps = Pick<
  MainAppPageProps,
  | "csrfToken"
  | "routes"
  | "orgSetupForm"
  | "incomingOrgInvitations"
  | "tradeOptions"
  | "userDisplay"
>;

export const OrgSetupPage: React.FC<OrgSetupPageProps> = ({
  csrfToken,
  routes,
  orgSetupForm,
  incomingOrgInvitations = [],
  tradeOptions = [],
  userDisplay,
}) => {
  const action = routes.organizationSetup ?? "/organization/setup";
  const needsDisplayName = Boolean(userDisplay?.needsDisplayName);
  const hasIncomingInvites = incomingOrgInvitations.length > 0;
  const hasSubmittedCreateForm = Boolean(
    orgSetupForm?.name?.trim() ||
    orgSetupForm?.phone?.trim() ||
    orgSetupForm?.tradeSlug?.trim(),
  );
  const [showCreateOwn, setShowCreateOwn] = useState(
    hasIncomingInvites && hasSubmittedCreateForm,
  );
  const [name, setName] = useState(orgSetupForm?.name ?? "");
  const [ownerName, setOwnerName] = useState(orgSetupForm?.ownerName ?? "");
  const [phone, setPhone] = useState(orgSetupForm?.phone ?? "");
  const [tradeSlug, setTradeSlug] = useState(orgSetupForm?.tradeSlug ?? "");
  const [tradeType, setTradeType] = useState(orgSetupForm?.tradeType ?? "");
  const showCreateOrganization = !hasIncomingInvites || showCreateOwn;
  const canCreateOrganization = CreateOrganizationSchema.safeParse({
    name,
    ownerName,
    phone,
    tradeSlug,
    tradeType,
  }).success;

  const createOrganizationCard = (
    <SurfaceCard className="rounded-2xl border-border p-8 shadow-lg">
      <h2 className="mb-4 text-base font-semibold text-foreground">
        Create your own
      </h2>
      <form action={action} method="post">
        <AuthenticityTokenField csrfToken={csrfToken} />
        <div className="mb-4">
          <FieldLabel htmlFor="organization_name">Organization name</FieldLabel>
          <TextInput
            id="organization_name"
            name="organization[name]"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Alex Plumbing Co"
            required
            maxLength={100}
          />
        </div>
        <div className="mb-4">
          <FieldLabel htmlFor="organization_owner_name">Your name</FieldLabel>
          <TextInput
            id="organization_owner_name"
            name="organization[owner_name]"
            value={ownerName}
            onChange={(event) => setOwnerName(event.target.value)}
            placeholder="Alex Builder"
            required
            maxLength={100}
          />
        </div>
        <div className="mb-4">
          <FieldLabel htmlFor="organization_phone">Phone</FieldLabel>
          <TextInput
            id="organization_phone"
            name="organization[phone]"
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="555-000-0000"
            required
            maxLength={40}
          />
        </div>
        <OrgTradeFields
          tradeOptions={tradeOptions}
          tradeSlug={tradeSlug}
          tradeType={tradeType}
          onTradeChange={(next) => {
            setTradeSlug(next.tradeSlug);
            setTradeType(next.tradeType);
          }}
        />
        <Button
          type="submit"
          variant="premium"
          className="mt-2 w-full py-3 font-medium"
          disabled={!canCreateOrganization}
        >
          Create organization
        </Button>
      </form>
    </SurfaceCard>
  );

  return (
    <AuthPageLayout className="max-w-lg">
      <h1 className="mb-8 text-center font-display text-3xl font-semibold tracking-tight text-foreground">
        Set up your company
      </h1>

      {hasIncomingInvites ? (
        <IncomingOrgInvitationsCard
          csrfToken={csrfToken}
          routes={routes}
          invitations={incomingOrgInvitations}
          needsDisplayName={needsDisplayName}
          variant="highlight"
        />
      ) : null}

      {hasIncomingInvites ? (
        <Button
          type="button"
          variant="ghost"
          className="mt-6 w-full text-muted-foreground"
          onClick={() => setShowCreateOwn((open) => !open)}
        >
          {showCreateOwn
            ? "Hide create company form"
            : "Or create your own company"}
        </Button>
      ) : null}

      {showCreateOrganization ? (
        <div className={hasIncomingInvites ? "mt-4" : undefined}>
          {createOrganizationCard}
        </div>
      ) : null}
    </AuthPageLayout>
  );
};
